import { Group, isCheckboxEntryEdited, isSelectEntryEdited } from '@bpmn-io/properties-panel';
import { useCallback, useEffect, useMemo, useRef } from '@bpmn-io/properties-panel/preact/hooks';
import { jsx } from '@bpmn-io/properties-panel/preact/jsx-runtime';
import { getBusinessObject, is, isAny } from 'bpmn-js/lib/util/ModelUtil';
import { getExtensionElementsList } from 'camunda-bpmn-js/lib/util/ExtensionElementsUtil';
import { createRoot, type Root } from 'react-dom/client';
import { renderPeoplePicker, type TPeoplePickerOption } from '../../../../pickers/PeoplePicker';
import { renderPeopleFieldPicker } from '../../../../pickers/PeopleFieldPicker';
import type { ServiceMap } from '../../../types/didi';
import type { BpmnElement, EntryDefinition, GroupFn } from '../../../types/properties-panel';
import { useService } from '../hooks/useService';
import { createElement } from '../util/factory-util';
import { createOption, SelectEntry } from './SelectEntry';
import type { Injector } from 'didi';
import { CheckboxEntry } from './CheckboxEntry';

const groupId = 'taskPermissionsGroup';

export const TaskPermissionsGroup: GroupFn = (element, injector) => {
    if (
        !is(element, 'bpmn:UserTask') &&
        isAny(element, ['bpmn:Task', 'bpmn:ServiceTask', 'bpmn:ScriptTask' /*, 'bpmn:UserTask'*/])
    ) {
        const entries = [
            {
                component: BreakInheritance,
                id: 'breakInheritance',
                isEdited: isSelectEntryEdited,
            },
        ];

        if (getPermissions(element)?.breakPermissionInheritance === '1') {
            entries.push(
                {
                    component: MaintainExisting,
                    id: 'maintainExisting',
                    isEdited: isCheckboxEntryEdited,
                },
                ...(editorEntries(injector) as any),
                ...(readerEntries(injector) as any)
            );
        }

        return {
            entries,
            id: groupId,
            label: injector.get('translate')('Permissions'),
            component: Group,
        };
    }
};

type GroupComp<P = {}> = EntryDefinition<{}, P>['component'];

const BreakInheritance: GroupComp = (props) => {
    const { element } = props;
    const value = getPermissions(element)?.breakPermissionInheritance ?? '';
    const bpmnFactory = useService('bpmnFactory');
    const commandStack = useService('commandStack');
    const modeling = useService('modeling');
    const translate = useService('translate');

    const options = useMemo(
        () => [
            createOption(translate('Unaltered'), '0'),
            createOption(translate('Interrupt'), '1'),
            createOption(translate('Reset'), '2'),
        ],
        []
    );

    return SelectEntry({
        element,
        getOptions: useCallback(
            () =>
                value
                    ? options
                    : [createOption(translate('Choose from'), '', true)].concat(options),
            [options, value]
        ),
        getValue: () => value,
        id: 'breakInheritance',
        label: translate('Permissions'), // translate('Break permission inheritance'),
        setValue: (newValue) => {
            modeling.updateModdleProperties(
                element,
                ensurePermissions(element, bpmnFactory, commandStack),
                {
                    breakPermissionInheritance: newValue,
                }
            );
        },
    });
};

const MaintainExisting: GroupComp = (props) => {
    const { element } = props;
    const bpmnFactory = useService('bpmnFactory');
    const commandStack = useService('commandStack');
    const modeling = useService('modeling');
    const translate = useService('translate');

    return CheckboxEntry({
        element,
        getValue: () => (getPermissions(element)?.maintainExistingPermissions ?? false) === true,
        id: 'maintainExisting',
        label: translate('Maintain existing permissions'),
        setValue: (maintainExistingPermissions) => {
            modeling.updateModdleProperties(
                element,
                ensurePermissions(element, bpmnFactory, commandStack),
                { maintainExistingPermissions }
            );
        },
    });
};

const editorEntries = (injector: Injector<ServiceMap>) => {
    const translate = injector.get('translate');
    const permissionsPropName = 'editorPermissions';
    const permissionsType = 'EditorPermissions';
    return [
        {
            component: Principals,
            id: 'editors',
            isEdited: () => false,
            label: translate('Editors'),
            permissionsPropName,
            permissionsType,
            principalsPropName: 'users',
            principalType: 1,
        },
        {
            component: Principals,
            id: 'editorialGroup',
            isEdited: () => false,
            label: translate('Editorial group'),
            permissionsPropName,
            permissionsType,
            principalsPropName: 'groups',
            principalType: 8,
        },
        {
            component: PrincipalFields,
            id: 'editorFields',
            isEdited: () => false,
            label: translate('Editors from fields'),
            permissionsPropName,
            permissionsType,
            principalsPropName: 'fields',
        },
    ];
};

const readerEntries = (injector: Injector<ServiceMap>) => {
    const translate = injector.get('translate');
    const permissionsPropName = 'readerPermissions';
    const permissionsType = 'ReaderPermissions';
    return [
        {
            component: Principals,
            id: 'readers',
            isEdited: () => false,
            label: translate('Readers'),
            permissionsPropName,
            permissionsType,
            principalsPropName: 'users',
            principalType: 1,
        },
        {
            component: Principals,
            id: 'readerGroup',
            isEdited: () => false,
            label: translate('Reader group'),
            permissionsPropName,
            permissionsType,
            principalsPropName: 'groups',
            principalType: 8,
        },
        {
            component: PrincipalFields,
            id: 'readerFields',
            isEdited: () => false,
            label: translate('Readers from fields'),
            permissionsPropName,
            permissionsType,
            principalsPropName: 'fields',
        },
    ];
};

const Principals: GroupComp<{
    element: any;
    label: string;
    permissionsType: SubPermissionType;
    permissionsPropName: SubPermissionPropName;
    principalsPropName: PrincipalsPropName;
    principalType: 1 | 8;
}> = (props) => {
    const containerRef = useRef<HTMLDivElement>();
    const rootRef = useRef<Root>();
    const {
        element,
        label,
        permissionsPropName,
        permissionsType,
        principalsPropName,
        principalType,
    } = props;
    const siteUrl: string | undefined =
        useService('canvas').getRootElement().businessObject.siteUrl;
    const bpmnFactory = useService('bpmnFactory');
    const commandStack = useService('commandStack');
    const modeling = useService('modeling');
    const translate = useService('translate');

    const principals: Record<'email' | 'id' | 'loginName' | 'name', string>[] | undefined =
        getSubPermissionsProp(props.element, permissionsPropName, principalsPropName)?.values;

    const value = useMemo<TPeoplePickerOption[]>(
        () =>
            Array.isArray(principals)
                ? principals.map(({ email, id, loginName, name }) => ({
                      email,
                      // id,
                      loginName,
                      memberId: id,
                      name,
                  }))
                : [],
        [principals]
    );

    useEffect(() => {
        const root = rootRef.current || createRoot(containerRef.current!);
        rootRef.current = root;
        renderPeoplePicker(root, {
            label,
            onChange: (value) => {
                if (value.length > 0) {
                    modeling.updateModdleProperties(
                        element,
                        ensureSubPermissionsProp(
                            props.element,
                            bpmnFactory,
                            commandStack,
                            modeling,
                            permissionsType,
                            permissionsPropName,
                            principalsPropName,
                            principalType === 8 ? 'Groups' : 'Users'
                        ),
                        {
                            values: value.map(({ email, loginName, memberId, name }) =>
                                bpmnFactory.create('fism:Principal', {
                                    email,
                                    id: memberId,
                                    loginName,
                                    name,
                                })
                            ),
                        }
                    );
                } else {
                    modeling.updateModdleProperties(
                        element,
                        getSubPermissions(props.element, permissionsPropName)!,
                        {
                            [principalsPropName]: undefined,
                        }
                    );
                }
            },
            principalType,
            siteUrl,
            translate,
            value,
        });
    }, [label, permissionsPropName, permissionsType, principalsPropName, siteUrl, value]);

    useEffect(
        () => () => {
            if (rootRef.current) {
                rootRef.current.unmount();
            }
        },
        []
    );

    return useMemo(
        () =>
            // @ts-ignore
            jsx('div', {
                className: 'bio-properties-panel-entry',
                ref: containerRef,
            }),
        []
    );
};

const PrincipalFields: GroupComp<{
    element: any;
    label: string;
    permissionsType: SubPermissionType;
    permissionsPropName: SubPermissionPropName;
    // principalsPropName: PrincipalsPropName;
}> = (props) => {
    const containerRef = useRef<HTMLDivElement>();
    const rootRef = useRef<Root>();
    const {
        element,
        label,
        permissionsPropName,
        permissionsType,
        // principalsPropName,
        // principalType,
    } = props;

    const rbo = useService('canvas').getRootElement().businessObject;
    const siteUrl: string | undefined = rbo.siteUrl;
    const list: string | undefined = rbo.listId;

    const bpmnFactory = useService('bpmnFactory');
    const commandStack = useService('commandStack');
    const modeling = useService('modeling');
    const translate = useService('translate');

    const fields = getSubPermissionsProp(props.element, permissionsPropName, 'fields')?.values;

    const value = useMemo(
        () =>
            Array.isArray(fields)
                ? fields.map(({ displayName, internalName }) => ({ displayName, internalName }))
                : [],
        [fields]
    );

    useEffect(() => {
        const root = rootRef.current || createRoot(containerRef.current!);
        rootRef.current = root;
        renderPeopleFieldPicker(root, {
            label,
            list,
            onChange: (value) => {
                if (value.length > 0) {
                    modeling.updateModdleProperties(
                        element,
                        ensureSubPermissionsProp(
                            props.element,
                            bpmnFactory,
                            commandStack,
                            modeling,
                            permissionsType,
                            permissionsPropName,
                            'fields',
                            'Fields'
                        ),
                        {
                            values: value.map(({ displayName, internalName }) =>
                                bpmnFactory.create('fism:Field', { displayName, internalName })
                            ),
                        }
                    );
                } else {
                    modeling.updateModdleProperties(
                        element,
                        getSubPermissions(props.element, permissionsPropName)!,
                        { fields: undefined }
                    );
                }
            },
            siteUrl,
            translate,
            value,
        });
    }, [label, permissionsPropName, permissionsType, siteUrl, list, value]);

    useEffect(
        () => () => {
            if (rootRef.current) {
                rootRef.current.unmount();
            }
        },
        []
    );

    return useMemo(
        () =>
            // @ts-ignore
            jsx('div', {
                className: 'bio-properties-panel-entry',
                ref: containerRef,
            }),
        []
    );
};

const getPermissions = (element: BpmnElement): any | undefined =>
    getExtensionElementsList(element, 'fism:Permissions')[0];

const ensurePermissions = (
    element: BpmnElement,
    bpmnFactory: ServiceMap['bpmnFactory'],
    commandStack: ServiceMap['commandStack']
): any => {
    let permissions = getPermissions(element);

    if (permissions) {
        return permissions;
    }

    const businessObject = getBusinessObject(element);
    let extensionElements = businessObject.extensionElements;
    const commands = [];

    // (1) ensure extension elements
    if (!extensionElements) {
        extensionElements = createElement(
            bpmnFactory,
            'bpmn:ExtensionElements',
            { values: [] },
            businessObject
        );

        commands.push({
            cmd: 'element.updateModdleProperties',
            context: {
                element,
                moddleElement: businessObject,
                properties: { extensionElements },
            },
        });
    }

    // (2) ensure permissions element
    permissions = createElement(bpmnFactory, 'fism:Permissions', {}, extensionElements);

    commands.push({
        cmd: 'element.updateModdleProperties',
        context: {
            element,
            moddleElement: extensionElements,
            properties: {
                values: [...extensionElements.get('values'), permissions],
            },
        },
    });

    commandStack.execute('properties-panel.multi-command-executor', commands);
    return permissions;
};

type SubPermissionPropName = 'editorPermissions' | 'readerPermissions';

const getSubPermissions = (
    element: BpmnElement,
    propName: SubPermissionPropName
): any | undefined => {
    const permissions = getPermissions(element);
    return permissions ? permissions[propName] : undefined;
};

type SubPermissionType = 'EditorPermissions' | 'ReaderPermissions';

const ensureSubPermissions = (
    element: BpmnElement,
    bpmnFactory: ServiceMap['bpmnFactory'],
    commandStack: ServiceMap['commandStack'],
    modeling: ServiceMap['modeling'],
    propName: SubPermissionPropName,
    type: SubPermissionType
): any => {
    const permissions = ensurePermissions(element, bpmnFactory, commandStack);
    let subPermissions = getSubPermissions(element, propName);

    if (subPermissions) {
        return subPermissions;
    }

    subPermissions = createElement(bpmnFactory, 'fism:' + type, {}, permissions);
    modeling.updateModdleProperties(element, permissions, { [propName]: subPermissions });
    return subPermissions;
};

const getSubPermissionsProp = (
    element: BpmnElement,
    permissionsPropName: SubPermissionPropName,
    propName: PrincipalsPropName | 'fields'
): any | undefined => {
    const subPermissions = getSubPermissions(element, permissionsPropName);
    return subPermissions ? subPermissions[propName] : undefined;
};

type PrincipalsPropName = 'users' | 'groups';

const ensureSubPermissionsProp = (
    element: BpmnElement,
    bpmnFactory: ServiceMap['bpmnFactory'],
    commandStack: ServiceMap['commandStack'],
    modeling: ServiceMap['modeling'],
    permissionsType: SubPermissionType,
    permissionsPropName: SubPermissionPropName,
    // principalsType: 1 | 8,
    // principalsPropName: PrincipalsPropName
    propName: PrincipalsPropName | 'fields',
    schemaType: 'Fields' | 'Groups' | 'Users'
): any => {
    const subPermissions = ensureSubPermissions(
        element,
        bpmnFactory,
        commandStack,
        modeling,
        permissionsPropName,
        permissionsType
    );
    let principals = getSubPermissionsProp(element, permissionsPropName, propName);

    if (principals) {
        return principals;
    }

    principals = createElement(
        bpmnFactory,
        'fism:' + schemaType, // (principalsType === 8 ? 'Groups' : 'Users'),
        {},
        subPermissions
    );
    modeling.updateModdleProperties(element, subPermissions, { [propName]: principals });
    return principals;
};
